import { Suspense, lazy, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "./store/redux/slices/authSlice";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
// Login is the site's landing route, so it's kept as an eager import; every
// other screen is lazy-loaded so the login page's initial JS/CSS payload
// doesn't include code it never uses.
import Login from "./screens/login/Login";
import { MobileMenuProvider } from "./context/MobileMenuContext";
import BurgerMenu from "./components/BurgerMenu";
import GlobalLoader from "./components/GlobalLoader";
import InactivityLogout from "./components/InactivityLogout";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import lazyWithRetry from "./utils/lazyWithRetry";

// These screens' JS is still code-split (imported below via lazy()), but
// their CSS is imported here eagerly so it's part of the initial bundle.
// Webpack loads a lazy chunk's CSS asynchronously alongside its JS, and on
// a real (non-instant) network that CSS can lag a beat behind the JS —
// which showed up as unstyled tab bars / broken layouts on first visit to
// a route, fixed only by navigating away and back once the chunk was
// cached. Loading all of it upfront removes that race entirely.
import "./components/Sidebar.css";
import "./components/MainContent.css";
import "./components/NavItem.css";
import "./components/jobCard/AttachmentTab.css";
import "./components/tir/AttachmentsTab.css";
import "./components/common/ImageViewer.css";
import "./screens/dashboard/Dashboard.css";
import "./screens/welcome/Welcome.css";
import "./screens/complaints/JobCard.css";
import "./screens/complaints/TIR.css";
import "./screens/complaints/SearchVehicle.css";
import "./screens/complaints/GatePass.css";
import "./screens/salesQuotation/SalesQuotation.css";
import "./screens/banking/IncomingPayments.css";
import "./screens/banking/OutgoingPayments.css";
import "./screens/changePassword/ChangePassword.css";
import "./screens/excelPaste/ExcelPaste.css";
import "./screens/notfound/NotFound.css";

const Complaints = lazy(lazyWithRetry(() => import("./screens/complaints/Complaints")));
const NotFound = lazy(lazyWithRetry(() => import("./screens/notfound/NotFound")));
const Dashboard = lazy(lazyWithRetry(() => import("./screens/dashboard/Dashboard")));
const Welcome = lazy(lazyWithRetry(() => import("./screens/welcome/Welcome")));
const Purchase = lazy(lazyWithRetry(() => import("./screens/purchase/Purchase")));
const Banking = lazy(lazyWithRetry(() => import("./screens/banking/Banking")));
const MasterData = lazy(lazyWithRetry(() => import("./screens/masterData/MasterData")));
const Reports = lazy(lazyWithRetry(() => import("./screens/reports/Reports")));
const ExcelPaste = lazy(lazyWithRetry(() => import("./screens/excelPaste/ExcelPaste")));
const ChangePassword = lazy(lazyWithRetry(() => import("./screens/changePassword/ChangePassword")));
const SalesQuotation = lazy(lazyWithRetry(() => import("./screens/salesQuotation/SalesQuotation")));

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useSelector((state) => state.auth);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children ? children : <Outlet />;
};

// Public Route Component (for login)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  return isAuthenticated ? <Navigate to={"/welcome"} replace /> : children;
};

function App() {
  const showGlobalLoaderPreview = false; // Set to true to preview GlobalLoader
  const dispatch = useDispatch();
  const { token, user } = useSelector((state) => state.auth);

  // On load: stay logged in only if both token and user.userName and user?.branchName exist; otherwise logout
  useEffect(() => {
    const hasToken = token != null && token !== "";
    const hasDealerName = user?.userName != null && String(user.userName).trim() !== "";
    const hasDealerBranch = user?.branchName != null && String(user.branchName).trim() !== "";

    if (hasToken || user != null) {
      if (!hasToken || !hasDealerName || !hasDealerBranch) {
        dispatch(logout());
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount after rehydration

  return (
    <MobileMenuProvider>
      <GlobalLoader show={showGlobalLoaderPreview} />
      <div className="app-container">
        <Router>
          <BurgerMenu />
          <InactivityLogout />
          <main>
          <RouteErrorBoundary>
          <Suspense fallback={<GlobalLoader show />}>
            <Routes>
            {/* Redirect root path to /login */}
            <Route path="/" element={<Navigate to="/login" replace />} />

            {/* Login route with public protection */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />

            {/* Protected routes */}
            <Route
              path="/welcome"
              element={
                <ProtectedRoute>
                  <Welcome />
                </ProtectedRoute>
              }
            />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            {/* Protected routes */}
            <Route
              path="/salesQuotation"
              element={
                <ProtectedRoute>
                  <SalesQuotation />
                </ProtectedRoute>
              }
            />

            {/* Protected routes */}
            <Route
              path="/purchase/*"
              element={
                <ProtectedRoute>
                  <Purchase />
                </ProtectedRoute>
              }
            />

            {/* Protected routes */}
            <Route
              path="/masterData/*"
              element={
                <ProtectedRoute>
                  <MasterData />
                </ProtectedRoute>
              }
            />

            {/* Protected routes */}
            <Route
              path="/banking/*"
              element={
                <ProtectedRoute>
                  <Banking />
                </ProtectedRoute>
              }
            />

            {/* Protected routes */}
            <Route
              path="/complaints/*"
              element={
                <ProtectedRoute>
                  <Complaints />
                </ProtectedRoute>
              }
            />

            {/* Protected routes */}
            <Route
              path="/reports/*"
              element={
                <ProtectedRoute>
                  <Reports />
                </ProtectedRoute>
              }
            />

            {/* Protected routes */}
            {/* <Route
              path="/userManagement"
              element={
                <ProtectedRoute>
                  <UserManagement />
                </ProtectedRoute>
              }
            /> */}
            
            {/* Protected routes */}
            <Route
              path="/changePassword"
              element={
                <ProtectedRoute>
                  <ChangePassword />
                </ProtectedRoute>
              }
            />

            {/* Protected routes */}
            <Route
              path="/excelPaste"
              element={
                <ProtectedRoute>
                  <ExcelPaste />
                </ProtectedRoute>
              }
            />

            {/* Catch-all route for 404 Not Found */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          </RouteErrorBoundary>
          </main>
        </Router>
      </div>
    </MobileMenuProvider>
  );
}

export default App;
